/**
 * Spec 196 / 198 — typed client for the Gemini source-domain repair route.
 *
 * Gemini returns its grounding sources behind a `vertexaisearch.cloud.google.com`
 * redirect, so every Gemini source was attributed to that single host. This
 * endpoint re-reads responses GEO Observatory already has and re-attributes each source
 * to the publisher named in the grounding title. It recovers the *publisher
 * domain* only — the specific page URL behind the redirect is not recoverable —
 * and makes no new engine API calls.
 *
 * Spec 198 — the run is TWO phases, each driven request-by-request from here:
 *
 *   1. repair  — keyset cursor over responses (`after_id`, bounded `limit`),
 *                re-attributing sources. Rescoring no longer happens here, so
 *                `periods_rescored` is honestly 0 for every repair response.
 *   2. rescore — keyset cursor over the affected score periods
 *                (`after_period` + `after_brand_id`, bounded `period_limit`),
 *                recomputing each bucket exactly once after all of its
 *                responses have been repaired.
 *
 * Phase 1 previously rescored every touched period on every batch, which made a
 * single request's work grow without bound (and timed out on real brands); it
 * also froze partial-repair values into periods that straddled a batch boundary.
 * Bounding both phases per request makes a timeout much less likely — it cannot
 * make one impossible — and a run that stops early is safe to re-run: both
 * phases are idempotent and resume from the start of their own cursor.
 *
 *   POST /api/v1/admin/backfill/gemini-source-domains
 *     repair:  { brand_id, after_id?, limit? }
 *     rescore: { brand_id, phase: 'rescore', after_period?, after_brand_id?, period_limit? }
 *
 * Admin-gated and brand-access scoped server-side; idempotent.
 */
import { apiFetch } from '../lib/api.js';

/** Which half of the run a request/response belongs to. */
export type GeminiBackfillPhase = 'repair' | 'rescore';

/**
 * Fields present on every response from the route, in either phase. The rescore
 * phase corrects no responses, so it returns these zeroed/null rather than
 * dropping them — the client can accumulate them unconditionally.
 */
export interface GeminiSourceDomainBatchBase {
  /** Responses examined in this batch. */
  processed: number;
  /** Responses whose sources were successfully re-attributed. */
  responses_reattributed: number;
  responses_failed: number;
  /** Sources whose attribution moved off the redirect host. */
  sources_reattributed: number;
  /** Sources that had to fall back (title absent / not a bare domain). */
  sources_unattributed: number;
  last_id: string | null;
  done: boolean;
  remaining: number;
  /** Score buckets recomputed by THIS request. Always 0 in the repair phase. */
  periods_rescored: number;
  brands: number;
}

/** One repair-phase batch: keyset over responses via `last_id`. */
export interface GeminiRepairBatchResult extends GeminiSourceDomainBatchBase {
  phase: 'repair';
  /** Present only once the repair phase is `done` — the handoff to phase 2. */
  next_phase?: GeminiBackfillPhase;
}

/** One rescore-phase batch: keyset over score periods via `last_period`. */
export interface GeminiRescoreBatchResult extends GeminiSourceDomainBatchBase {
  phase: 'rescore';
  /** Buckets this request tried to recompute and could not. */
  periods_failed: number;
  /** `YYYY-MM-DD` cursor for the next request, or null when nothing advanced. */
  last_period: string | null;
  last_brand_id: string | null;
  /** Buckets still queued after this request's cursor. */
  periods_remaining: number;
  /** The request hit its own work budget and returned early (`done` is false). */
  budget_stopped: boolean;
}

/** One batch result from either phase, discriminated on `phase`. */
export type GeminiSourceDomainBatchResult = GeminiRepairBatchResult | GeminiRescoreBatchResult;

/** Accumulated totals across the whole two-phase run. */
export interface GeminiSourceDomainRepairResult {
  responses_reattributed: number;
  responses_failed: number;
  sources_reattributed: number;
  sources_unattributed: number;
  /** Summed across BOTH phases — see the accumulation note in the loop below. */
  periods_rescored: number;
  periods_failed: number;
  batches: number;
}

export interface GeminiSourceDomainProgress {
  /** Which half of the run is currently executing. */
  phase: GeminiBackfillPhase;
  /** Responses re-attributed so far across all repair batches. */
  reattributed: number;
  /** Live count of responses still queued after the current repair cursor. */
  remaining: number;
  /** Live count of score periods still queued after the current rescore cursor. */
  periodsRemaining: number;
}

/**
 * Run one repair-phase keyset batch. Throws Error on non-2xx (matching the
 * backfillAioCitationsBatch pattern in aioCitations.ts).
 */
export async function backfillGeminiSourceDomainsBatch(
  brandId: string,
  afterId: string | null = null,
  limit?: number,
): Promise<GeminiRepairBatchResult> {
  const body: Record<string, unknown> = { brand_id: brandId };
  if (afterId) body.after_id = afterId;
  if (limit) body.limit = limit;
  const res = await apiFetch(`/api/v1/admin/backfill/gemini-source-domains`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Gemini source domain repair failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: GeminiRepairBatchResult };
  return json.data;
}

/**
 * Run one rescore-phase keyset batch. Same error-handling contract as the
 * repair batch above. The cursor is composite — `(period, brand_id)` — because
 * a period can span several brands in the multi-brand scope.
 */
export async function backfillGeminiRescoreBatch(
  brandId: string,
  afterPeriod: string | null = null,
  afterBrandId: string | null = null,
  periodLimit?: number,
): Promise<GeminiRescoreBatchResult> {
  const body: Record<string, unknown> = { brand_id: brandId, phase: 'rescore' };
  if (afterPeriod) body.after_period = afterPeriod;
  if (afterBrandId) body.after_brand_id = afterBrandId;
  if (periodLimit) body.period_limit = periodLimit;
  const res = await apiFetch(`/api/v1/admin/backfill/gemini-source-domains`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Gemini source domain repair failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: GeminiRescoreBatchResult };
  return json.data;
}

/**
 * Hard caps on the number of requests each phase may make. MAX_LIMIT(50) x 20000
 * responses and MAX_PERIOD_LIMIT(25) x 20000 periods are both far beyond any real
 * brand; they exist so a server that ever returned done=false with no forward
 * progress cannot spin forever.
 *
 * Spec 198 — the two phases MUST NOT share a counter. A large brand's repair
 * could otherwise exhaust the shared budget and leave the rescore truncated
 * while the run still resolved, i.e. a failure shaped exactly like success.
 */
const MAX_REPAIR_BATCHES = 20000;
const MAX_RESCORE_BATCHES = 20000;

/**
 * Drive both phases to completion: repair every response, then recompute every
 * affected score period. `onProgress` fires after each batch (tagged with the
 * phase) so the UI can say which half is running. Resolves only once BOTH
 * phases report done, so the caller may treat resolution as "run finished".
 */
export async function runGeminiSourceDomainRepair(
  brandId: string,
  onProgress?: (p: GeminiSourceDomainProgress) => void,
  limit?: number,
  periodLimit?: number,
): Promise<GeminiSourceDomainRepairResult> {
  const totals: GeminiSourceDomainRepairResult = {
    responses_reattributed: 0,
    responses_failed: 0,
    sources_reattributed: 0,
    sources_unattributed: 0,
    periods_rescored: 0,
    periods_failed: 0,
    batches: 0,
  };

  // ---- Phase 1: repair, keyed on `last_id` --------------------------------
  let cursor: string | null = null;
  for (let i = 0; i < MAX_REPAIR_BATCHES; i++) {
    const batch: GeminiRepairBatchResult = await backfillGeminiSourceDomainsBatch(
      brandId,
      cursor,
      limit,
    );
    totals.responses_reattributed += batch.responses_reattributed;
    totals.responses_failed += batch.responses_failed;
    totals.sources_reattributed += batch.sources_reattributed;
    totals.sources_unattributed += batch.sources_unattributed;
    // Reads 0 in this phase today, but accumulate unconditionally: the total
    // must be the sum across BOTH phases, and this keeps the two accumulation
    // sites identical if the server ever rescores anything here again.
    totals.periods_rescored += batch.periods_rescored;
    totals.batches += 1;
    onProgress?.({
      phase: 'repair',
      reattributed: totals.responses_reattributed,
      remaining: batch.remaining,
      periodsRemaining: 0,
    });
    if (batch.done) break;
    // No forward progress (defensive): stop rather than spin.
    if (!batch.last_id || batch.last_id === cursor) break;
    cursor = batch.last_id;
  }

  // ---- Phase 2: rescore, keyed on `(last_period, last_brand_id)` ----------
  // Starts from a null cursor — the repair phase's cursor does not carry over.
  // `budget_stopped` is NOT a stop condition: the server already reports
  // `done: false` for a budget-stopped page, so following `done` is correct and
  // treating it as done would truncate the run.
  let periodCursor: string | null = null;
  let brandCursor: string | null = null;
  for (let j = 0; j < MAX_RESCORE_BATCHES; j++) {
    const batch: GeminiRescoreBatchResult = await backfillGeminiRescoreBatch(
      brandId,
      periodCursor,
      brandCursor,
      periodLimit,
    );
    totals.periods_rescored += batch.periods_rescored;
    totals.periods_failed += batch.periods_failed;
    totals.batches += 1;
    onProgress?.({
      phase: 'rescore',
      reattributed: totals.responses_reattributed,
      remaining: 0,
      periodsRemaining: batch.periods_remaining,
    });
    if (batch.done) break;
    // No forward progress (defensive), mirroring the repair guard. The cursor
    // is composite, so a repeated period is only a stall when the brand half
    // did not advance either.
    if (!batch.last_period) break;
    if (batch.last_period === periodCursor && batch.last_brand_id === brandCursor) break;
    periodCursor = batch.last_period;
    brandCursor = batch.last_brand_id;
  }

  return totals;
}
